import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { useSnackbar } from '../components/AppSnackbar';
import ConfirmDialog from '../components/ConfirmDialog';
import type { AssetCategory, AssetStatus, UpdateAssetDto } from '@shared/types/asset.types';

// ============================================================
// Constants
// ============================================================

const PREDEFINED_CATEGORIES: { value: string; label: string }[] = [
  { value: 'pc', label: 'PC' },
  { value: 'server', label: 'サーバー' },
  { value: 'nas', label: 'NAS' },
  { value: 'network_device', label: 'ネットワーク機器' },
  { value: 'monitor', label: 'モニター' },
  { value: 'printer', label: 'プリンター' },
  { value: 'software_license', label: 'ソフトウェアライセンス' },
  { value: 'smartphone', label: 'スマートフォン' },
  { value: 'tablet', label: 'タブレット' },
  { value: 'other', label: 'その他備品' },
];

const CATEGORY_LABELS: Record<string, string> = {
  pc: 'PC',
  server: 'サーバー',
  nas: 'NAS',
  network_device: 'ネットワーク機器',
  monitor: 'モニター',
  printer: 'プリンター',
  software_license: 'ソフトウェアライセンス',
  smartphone: 'スマートフォン',
  tablet: 'タブレット',
  other: 'その他備品',
};

const ASSET_STATUSES: { value: AssetStatus; label: string }[] = [
  { value: 'in_use', label: '使用中' },
  { value: 'in_storage', label: '保管中' },
  { value: 'under_repair', label: '修理中' },
  { value: 'disposed', label: '廃棄済み' },
  { value: 'lost', label: '紛失' },
];

// ============================================================
// Types
// ============================================================

interface FormData {
  assetNumber: string;
  assetName: string;
  category: string;
  manufacturer: string;
  modelNumber: string;
  serialNumber: string;
  purchaseDate: string;
  maintenanceDeadline: string;
  renewalDeadline: string;
  location: string;
  department: string;
  userName: string;
  managerId: string;
  customerId: string;
  status: AssetStatus | '';
  notificationDaysOverride: string;
  purchasedLicenses: string;
  usedLicenses: string;
  notes: string;
}

interface FormErrors {
  [key: string]: string | undefined;
}

interface UserOption {
  id: string;
  name: string;
  email: string;
}

interface CustomerOption {
  id: string;
  customerCode: string;
  name: string;
}

interface CategoryMasterOption {
  id: string;
  name: string;
}

interface ManufacturerMasterOption {
  id: string;
  name: string;
}

interface AssetDetail {
  id: string;
  assetNumber: string;
  assetName: string | null;
  category: AssetCategory;
  manufacturer: string | null;
  modelNumber: string | null;
  serialNumber: string | null;
  purchaseDate: string | null;
  maintenanceDeadline: string | null;
  renewalDeadline: string | null;
  location: string | null;
  department: string | null;
  userName: string | null;
  managerId: string | null;
  customerId: string | null;
  status: AssetStatus;
  notificationDaysOverride: number | null;
  purchasedLicenses: number | null;
  usedLicenses: number | null;
  notes: string | null;
}

// ============================================================
// Component
// ============================================================

const AssetEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<FormData>({
    assetNumber: '',
    assetName: '',
    category: '',
    manufacturer: '',
    modelNumber: '',
    serialNumber: '',
    purchaseDate: '',
    maintenanceDeadline: '',
    renewalDeadline: '',
    location: '',
    department: '',
    userName: '',
    managerId: '',
    customerId: '',
    status: '',
    notificationDaysOverride: '',
    purchasedLicenses: '',
    usedLicenses: '',
    notes: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formInitialized, setFormInitialized] = useState(false);

  // Fetch asset data
  const {
    data: asset,
    isLoading,
    isError,
    error: fetchError,
  } = useQuery<AssetDetail>({
    queryKey: ['asset', id],
    queryFn: async () => {
      const response = await api.get(`/assets/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  // Fetch users for manager dropdown
  const { data: users = [] } = useQuery<UserOption[]>({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await api.get('/users');
      return response.data.data ?? response.data;
    },
  });

  // Fetch customers for dropdown
  const { data: customersData } = useQuery<{ data: CustomerOption[] }>({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data;
    },
  });
  const customers: CustomerOption[] = customersData?.data ?? [];

  // Fetch category master
  const { data: categoryMasterData } = useQuery<{ data: CategoryMasterOption[] }>({
    queryKey: ['master-categories'],
    queryFn: async () => {
      const response = await api.get('/master/categories');
      return response.data;
    },
  });

  // Fetch manufacturer master
  const { data: manufacturerMasterData } = useQuery<{ data: ManufacturerMasterOption[] }>({
    queryKey: ['master-manufacturers'],
    queryFn: async () => {
      const response = await api.get('/master/manufacturers');
      return response.data;
    },
  });

  // Fetch existing categories from assets (fallback)
  const { data: dbCategories = [] } = useQuery<string[]>({
    queryKey: ['asset-categories'],
    queryFn: async () => {
      const response = await api.get('/assets/categories');
      return response.data;
    },
  });

  // Merge category options
  const categoryOptions = React.useMemo(() => {
    const masterNames = (categoryMasterData?.data ?? []).map((c) => c.name);
    const predefinedValues = PREDEFINED_CATEGORIES.map((c) => c.value);
    const allValues = new Set([...masterNames, ...predefinedValues, ...dbCategories]);
    return Array.from(allValues);
  }, [categoryMasterData, dbCategories]);

  // Manufacturer options
  const manufacturerOptions = React.useMemo(() => {
    return (manufacturerMasterData?.data ?? []).map((m) => m.name);
  }, [manufacturerMasterData]);

  // Helper: normalize date value to YYYY-MM-DD or empty string
  const normalizeDate = (value: string | null | undefined): string => {
    if (!value) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) return value.substring(0, 10);
    return '';
  };

  // Populate form with existing asset data
  useEffect(() => {
    if (asset && !formInitialized) {
      setFormData({
        assetNumber: asset.assetNumber || '',
        assetName: asset.assetName || '',
        category: asset.category || '',
        manufacturer: asset.manufacturer || '',
        modelNumber: asset.modelNumber || '',
        serialNumber: asset.serialNumber || '',
        purchaseDate: normalizeDate(asset.purchaseDate),
        maintenanceDeadline: normalizeDate(asset.maintenanceDeadline),
        renewalDeadline: normalizeDate(asset.renewalDeadline),
        location: asset.location || '',
        department: asset.department || '',
        userName: asset.userName || '',
        managerId: asset.managerId || '',
        customerId: asset.customerId || '',
        status: asset.status || '',
        notificationDaysOverride:
          asset.notificationDaysOverride != null ? String(asset.notificationDaysOverride) : '',
        purchasedLicenses:
          asset.purchasedLicenses != null ? String(asset.purchasedLicenses) : '',
        usedLicenses:
          asset.usedLicenses != null ? String(asset.usedLicenses) : '',
        notes: asset.notes || '',
      });
      setFormInitialized(true);
    }
  }, [asset, formInitialized]);

  // Update asset mutation
  const updateAssetMutation = useMutation({
    mutationFn: async (data: UpdateAssetDto) => {
      const response = await api.put(`/assets/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      showSuccess('資産を更新しました');
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['asset', id] });
      navigate(`/assets/${id}`);
    },
    onError: (error: unknown) => {
      const axiosError = error as {
        response?: { data?: { error?: string; message?: string; details?: { field: string; message: string }[] } };
      };
      const responseData = axiosError?.response?.data;

      if (responseData?.error === 'VALIDATION_ERROR' && responseData.details) {
        const serverErrors: FormErrors = {};
        responseData.details.forEach((detail) => {
          serverErrors[detail.field] = detail.message;
        });
        setErrors(serverErrors);
      } else {
        showError(responseData?.message || '資産の更新に失敗しました');
      }
    },
    onSettled: () => {
      setSubmitting(false);
    },
  });

  // ============================================================
  // Validation
  // ============================================================

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Required fields
    if (!formData.assetNumber.trim()) {
      newErrors.assetNumber = '資産番号は必須です';
    } else if (formData.assetNumber.length > 50) {
      newErrors.assetNumber = '資産番号は50文字以内で入力してください';
    }

    if (formData.assetName.length > 200) {
      newErrors.assetName = '資産名称は200文字以内で入力してください';
    }

    if (!formData.category) {
      newErrors.category = '資産区分は必須です';
    } else if (formData.category.length > 30) {
      newErrors.category = '資産区分は30文字以内で入力してください';
    }

    if (!formData.status) {
      newErrors.status = '状態は必須です';
    }

    // Max length checks
    if (formData.manufacturer.length > 100) {
      newErrors.manufacturer = 'メーカーは100文字以内で入力してください';
    }
    if (formData.modelNumber.length > 100) {
      newErrors.modelNumber = '型番は100文字以内で入力してください';
    }
    if (formData.serialNumber.length > 100) {
      newErrors.serialNumber = 'シリアル番号は100文字以内で入力してください';
    }
    if (formData.location.length > 200) {
      newErrors.location = '設置場所は200文字以内で入力してください';
    }
    if (formData.department.length > 100) {
      newErrors.department = '使用部署は100文字以内で入力してください';
    }
    if (formData.userName.length > 100) {
      newErrors.userName = '使用者は100文字以内で入力してください';
    }
    if (formData.notes.length > 1000) {
      newErrors.notes = '備考は1000文字以内で入力してください';
    }

    // Date format checks
    const datePattern = /^\d{4}-\d{2}-\d{2}$/;
    if (formData.purchaseDate && !datePattern.test(formData.purchaseDate)) {
      newErrors.purchaseDate = '日付はYYYY-MM-DD形式で入力してください';
    }
    if (formData.maintenanceDeadline && !datePattern.test(formData.maintenanceDeadline)) {
      newErrors.maintenanceDeadline = '日付はYYYY-MM-DD形式で入力してください';
    }
    if (formData.renewalDeadline && !datePattern.test(formData.renewalDeadline)) {
      newErrors.renewalDeadline = '日付はYYYY-MM-DD形式で入力してください';
    }

    // Notification days override
    if (formData.notificationDaysOverride) {
      const days = Number(formData.notificationDaysOverride);
      if (!Number.isInteger(days) || days < 0) {
        newErrors.notificationDaysOverride = '通知期限日数は0以上の整数で入力してください';
      }
    }

    // Software license fields
    if (formData.category === 'software_license') {
      if (!formData.purchasedLicenses) {
        newErrors.purchasedLicenses = '購入ライセンス数は必須です';
      } else {
        const purchased = Number(formData.purchasedLicenses);
        if (!Number.isInteger(purchased) || purchased < 1) {
          newErrors.purchasedLicenses = '購入ライセンス数は1以上の整数で入力してください';
        }
      }

      if (!formData.usedLicenses && formData.usedLicenses !== '0') {
        newErrors.usedLicenses = '使用ライセンス数は必須です';
      } else {
        const used = Number(formData.usedLicenses);
        if (!Number.isInteger(used) || used < 0) {
          newErrors.usedLicenses = '使用ライセンス数は0以上の整数で入力してください';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ============================================================
  // Handlers
  // ============================================================

  const handleTextChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSelectChange = (field: keyof FormData) => (e: SelectChangeEvent<string>) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    const dto: UpdateAssetDto = {
      assetNumber: formData.assetNumber.trim(),
      category: formData.category as AssetCategory,
      status: formData.status as AssetStatus,
    };

    // Optional text fields
    if (formData.assetName.trim()) dto.assetName = formData.assetName.trim();
    if (formData.manufacturer.trim()) dto.manufacturer = formData.manufacturer.trim();
    if (formData.modelNumber.trim()) dto.modelNumber = formData.modelNumber.trim();
    if (formData.serialNumber.trim()) dto.serialNumber = formData.serialNumber.trim();
    if (formData.purchaseDate) dto.purchaseDate = formData.purchaseDate;
    if (formData.maintenanceDeadline) dto.maintenanceDeadline = formData.maintenanceDeadline;
    if (formData.renewalDeadline) dto.renewalDeadline = formData.renewalDeadline;
    if (formData.location.trim()) dto.location = formData.location.trim();
    if (formData.department.trim()) dto.department = formData.department.trim();
    if (formData.userName.trim()) dto.userName = formData.userName.trim();
    if (formData.managerId) dto.managerId = formData.managerId;
    if (formData.customerId) dto.customerId = formData.customerId;
    if (formData.notificationDaysOverride) dto.notificationDaysOverride = Number(formData.notificationDaysOverride);
    if (formData.notes.trim()) dto.notes = formData.notes.trim();

    // Software license fields
    if (formData.category === 'software_license') {
      dto.purchasedLicenses = Number(formData.purchasedLicenses);
      dto.usedLicenses = Number(formData.usedLicenses);
    }

    updateAssetMutation.mutate(dto);
  };

  const handleCancel = () => {
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = () => {
    setCancelDialogOpen(false);
    navigate(-1);
  };

  const handleCancelDismiss = () => {
    setCancelDialogOpen(false);
  };

  // ============================================================
  // Loading / Error states
  // ============================================================

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    const axiosErr = fetchError as { response?: { status?: number } };
    const is404 = axiosErr?.response?.status === 404;
    return (
      <Box>
        <Typography variant="h5" color="error">
          {is404 ? '資産が見つかりません' : '資産データの読み込みに失敗しました'}
        </Typography>
        <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate('/assets')}>
          資産台帳一覧に戻る
        </Button>
      </Box>
    );
  }

  // ============================================================
  // Render
  // ============================================================

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 48px - 48px)', overflow: 'hidden' }}>
      {/* Fixed header */}
      <Box sx={{ flexShrink: 0, pb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          資産編集
        </Typography>
      </Box>

      {/* Scrollable content */}
      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
      <Paper sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit} noValidate autoComplete="off">
          <input type="text" name="prevent_autofill" style={{ display: 'none' }} tabIndex={-1} />
          <input type="password" name="prevent_autofill_pass" style={{ display: 'none' }} tabIndex={-1} />
          <Grid container spacing={2}>
            {/* 資産番号 */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="資産番号"
                value={formData.assetNumber}
                onChange={handleTextChange('assetNumber')}
                error={!!errors.assetNumber}
                helperText={errors.assetNumber}
                inputProps={{ maxLength: 50 }}
                disabled={submitting}
              />
            </Grid>

            {/* 資産名称 */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="資産名称"
                value={formData.assetName}
                onChange={handleTextChange('assetName')}
                error={!!errors.assetName}
                helperText={errors.assetName}
                inputProps={{ maxLength: 200 }}
                disabled={submitting}
              />
            </Grid>

            {/* 資産区分 */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={categoryOptions}
                getOptionLabel={(option) => CATEGORY_LABELS[option] || option}
                value={formData.category || null}
                onChange={(_event, newValue) => {
                  setFormData((prev) => ({ ...prev, category: newValue ?? '' }));
                  if (errors.category) {
                    setErrors((prev) => ({ ...prev, category: undefined }));
                  }
                }}
                onInputChange={(_event, newInputValue, reason) => {
                  if (reason === 'input') {
                    const match = PREDEFINED_CATEGORIES.find((c) => c.label === newInputValue);
                    setFormData((prev) => ({ ...prev, category: match ? match.value : newInputValue }));
                    if (errors.category) {
                      setErrors((prev) => ({ ...prev, category: undefined }));
                    }
                  }
                }}
                disabled={submitting}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="資産区分"
                    error={!!errors.category}
                    helperText={errors.category}
                  />
                )}
              />
            </Grid>

            {/* メーカー */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={manufacturerOptions}
                value={formData.manufacturer || null}
                onChange={(_event, newValue) => {
                  setFormData((prev) => ({ ...prev, manufacturer: newValue ?? '' }));
                }}
                onInputChange={(_event, newInputValue, reason) => {
                  if (reason === 'input') {
                    setFormData((prev) => ({ ...prev, manufacturer: newInputValue }));
                  }
                }}
                disabled={submitting}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="メーカー"
                    error={!!errors.manufacturer}
                    helperText={errors.manufacturer}
                  />
                )}
              />
            </Grid>

            {/* 型番 */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="型番"
                value={formData.modelNumber}
                onChange={handleTextChange('modelNumber')}
                error={!!errors.modelNumber}
                helperText={errors.modelNumber}
                inputProps={{ maxLength: 100 }}
                disabled={submitting}
              />
            </Grid>

            {/* シリアル番号 */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="シリアル番号"
                value={formData.serialNumber}
                onChange={handleTextChange('serialNumber')}
                error={!!errors.serialNumber}
                helperText={errors.serialNumber}
                inputProps={{ maxLength: 100 }}
                disabled={submitting}
              />
            </Grid>

            {/* 購入日 */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="購入日"
                type="date"
                value={formData.purchaseDate}
                onChange={handleTextChange('purchaseDate')}
                error={!!errors.purchaseDate}
                helperText={errors.purchaseDate}
                InputLabelProps={{ shrink: true }}
                disabled={submitting}
              />
            </Grid>

            {/* 保守期限 */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="保守期限"
                type="date"
                value={formData.maintenanceDeadline}
                onChange={handleTextChange('maintenanceDeadline')}
                error={!!errors.maintenanceDeadline}
                helperText={errors.maintenanceDeadline}
                InputLabelProps={{ shrink: true }}
                disabled={submitting}
              />
            </Grid>

            {/* 更新期限 */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="更新期限"
                type="date"
                value={formData.renewalDeadline}
                onChange={handleTextChange('renewalDeadline')}
                error={!!errors.renewalDeadline}
                helperText={errors.renewalDeadline}
                InputLabelProps={{ shrink: true }}
                disabled={submitting}
              />
            </Grid>

            {/* 設置場所 */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="設置場所"
                value={formData.location}
                onChange={handleTextChange('location')}
                error={!!errors.location}
                helperText={errors.location}
                inputProps={{ maxLength: 200 }}
                disabled={submitting}
              />
            </Grid>

            {/* 使用部署 */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="使用部署"
                value={formData.department}
                onChange={handleTextChange('department')}
                error={!!errors.department}
                helperText={errors.department}
                inputProps={{ maxLength: 100 }}
                disabled={submitting}
              />
            </Grid>

            {/* 使用者 */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="使用者"
                value={formData.userName}
                onChange={handleTextChange('userName')}
                error={!!errors.userName}
                helperText={errors.userName}
                inputProps={{ maxLength: 100 }}
                disabled={submitting}
              />
            </Grid>

            {/* 管理者 */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.managerId} disabled={submitting}>
                <InputLabel id="manager-label">管理者</InputLabel>
                <Select
                  labelId="manager-label"
                  value={formData.managerId}
                  onChange={handleSelectChange('managerId')}
                  label="管理者"
                >
                  <MenuItem value="">
                    <em>未選択</em>
                  </MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </MenuItem>
                  ))}
                </Select>
                {errors.managerId && <FormHelperText>{errors.managerId}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* 顧客 */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.customerId} disabled={submitting}>
                <InputLabel id="customer-label">顧客</InputLabel>
                <Select
                  labelId="customer-label"
                  value={formData.customerId}
                  onChange={handleSelectChange('customerId')}
                  label="顧客"
                >
                  <MenuItem value="">
                    <em>未選択</em>
                  </MenuItem>
                  {customers.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.customerCode} - {c.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.customerId && <FormHelperText>{errors.customerId}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* 状態 */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!errors.status} disabled={submitting}>
                <InputLabel id="status-label">状態</InputLabel>
                <Select
                  labelId="status-label"
                  value={formData.status}
                  onChange={handleSelectChange('status')}
                  label="状態"
                >
                  {ASSET_STATUSES.map((st) => (
                    <MenuItem key={st.value} value={st.value}>
                      {st.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* 通知期限日数 */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="通知期限日数"
                type="number"
                value={formData.notificationDaysOverride}
                onChange={handleTextChange('notificationDaysOverride')}
                error={!!errors.notificationDaysOverride}
                helperText={errors.notificationDaysOverride || '未入力の場合はマスタのデフォルト値を使用'}
                inputProps={{ min: 0 }}
                disabled={submitting}
              />
            </Grid>

            {/* ソフトウェアライセンス追加項目 */}
            {formData.category === 'software_license' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 'bold' }}>
                    ライセンス情報
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="購入ライセンス数"
                    type="number"
                    value={formData.purchasedLicenses}
                    onChange={handleTextChange('purchasedLicenses')}
                    error={!!errors.purchasedLicenses}
                    helperText={errors.purchasedLicenses}
                    inputProps={{ min: 1 }}
                    disabled={submitting}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="使用ライセンス数"
                    type="number"
                    value={formData.usedLicenses}
                    onChange={handleTextChange('usedLicenses')}
                    error={!!errors.usedLicenses}
                    helperText={errors.usedLicenses}
                    inputProps={{ min: 0 }}
                    disabled={submitting}
                  />
                </Grid>
              </>
            )}

            {/* 備考 */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="備考"
                multiline
                rows={4}
                value={formData.notes}
                onChange={handleTextChange('notes')}
                error={!!errors.notes}
                helperText={errors.notes}
                inputProps={{ maxLength: 1000 }}
                disabled={submitting}
              />
            </Grid>

            {/* Action buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={submitting}
                >
                  キャンセル
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                >
                  {submitting ? '更新中...' : '更新'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
      </Box>

      {/* Cancel confirm dialog */}
      <ConfirmDialog
        open={cancelDialogOpen}
        title="確認"
        message="編集内容が破棄されます。よろしいですか？"
        confirmLabel="はい"
        cancelLabel="いいえ"
        onConfirm={handleCancelConfirm}
        onCancel={handleCancelDismiss}
        severity="warning"
      />
    </Box>
  );
};

export default AssetEditPage;
